import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ChatBubble as CommentIcon,
  Delete as DeleteIcon,
  EditOutlined as EditIcon,
  FavoriteBorder as LikeIcon,
  Favorite as LikedIcon,
  Search as SearchIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import api from "../api/api";
import {
  FORUM_PAGE_SIZE,
  FORUM_TYPES,
  formatForumDate,
  getForumTitle,
  normalizeJobForumComment,
  normalizeJobForumLike,
  normalizeJobForumPost,
  saveForumState,
} from "./jobReferralStore";

const emptyForm = {
  forumType: "Job Referral",
  jobTitle: "",
  jobDescription: "",
  experience: "",
  companyName: "",
  locationCity: "",
  isActive: true,
  contactNumber: "",
};

function buildJobForumPostPayload(formData, currentUserId) {
  const isJobSeeking = formData.forumType === "Job Seeking";

  return {
    PostedBy: currentUserId,
    ForumType: formData.forumType,
    JobTitle: formData.jobTitle.trim(),
    JobDescription: formData.jobDescription.trim(),
    Experience: Number(formData.experience) || 0,
    CompanyName: isJobSeeking ? "" : formData.companyName.trim(),
    LocationCity: formData.locationCity.trim(),
    ContactNumber: formData.contactNumber.trim(),
    IsActive: Boolean(formData.isActive),
  };
}

function StatCard({ label, value, color }) {
  return (
    <Card
      sx={{
        borderRadius: 4,
        boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
      }}
    >
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {label}
        </Typography>
        <Typography variant="h4" fontWeight={800} color={color}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function JobReferal() {
  const userState = useSelector((state) => state.user);
  const currentUser = userState?.user || {};
  const [forumState, setForumState] = useState({
    posts: [],
    comments: [],
    likes: [],
  });
  const [search, setSearch] = useState("");
  const [forumTypeFilter, setForumTypeFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("active");
  const [postedByFilter, setPostedByFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmittingPost, setIsSubmittingPost] = useState(false);
  const [isSubmittingLikeId, setIsSubmittingLikeId] = useState(null);
  const [isLoadingForum, setIsLoadingForum] = useState(true);
  const [postFormError, setPostFormError] = useState("");

  const currentUserId =
    Number(currentUser.userId || currentUser.id || currentUser.Id) || 9999;
  const currentUserName =
    `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim() ||
    "Current Alumni";
  const currentUserYear = currentUser.passedOutYear || "Batch not set";
  const currentUserToken =
    currentUser.token || currentUser.accessToken || currentUser.jwtToken || "";

  useEffect(() => {
    saveForumState(forumState);
  }, [forumState]);

  useEffect(() => {
    let isMounted = true;

    async function fetchForumData() {
      try {
        setIsLoadingForum(true);
        const [postsResponse, commentsResponse, likesResponse] = await Promise.all([
          api.getJobForumPosts(currentUserToken),
          api.getJobForumComments(currentUserToken),
          api.getJobForumLikes(currentUserToken),
        ]);

        const rawPosts = Array.isArray(postsResponse?.data)
          ? postsResponse.data
          : postsResponse?.data?.posts || postsResponse?.data?.data || [];
        const rawComments = Array.isArray(commentsResponse?.data)
          ? commentsResponse.data
          : commentsResponse?.data?.comments || commentsResponse?.data?.data || [];
        const rawLikes = Array.isArray(likesResponse?.data)
          ? likesResponse.data
          : likesResponse?.data?.likes || likesResponse?.data?.data || [];

        const likes = rawLikes.map(normalizeJobForumLike);
        const posts = rawPosts.map((post) => {
          const normalizedPost = normalizeJobForumPost(post);
          return {
            ...normalizedPost,
            likedBy: likes
              .filter((like) => Number(like.postId) === Number(normalizedPost.id))
              .map((like) => like.likedBy),
          };
        });
        const comments = rawComments.map(normalizeJobForumComment);

        if (!isMounted) {
          return;
        }

        setForumState({ posts, comments, likes });
      } catch (error) {
        console.error("Failed to load forum data", error);
        if (isMounted) {
          setForumState({ posts: [], comments: [], likes: [] });
        }
      } finally {
        if (isMounted) {
          setIsLoadingForum(false);
        }
      }
    }

    fetchForumData();

    return () => {
      isMounted = false;
    };
  }, [currentUserToken]);

  const commentsCountMap = useMemo(() => {
    return forumState.comments.reduce((acc, comment) => {
      acc[comment.postId] = (acc[comment.postId] || 0) + 1;
      return acc;
    }, {});
  }, [forumState.comments]);

  const filteredPosts = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return [...forumState.posts]
      .filter((post) => {
        const matchesSearch =
          !normalizedSearch ||
          [
            post.jobTitle,
            post.posterName,
            post.companyName,
            post.locationCity,
            post.jobDescription,
            post.forumType,
          ]
            .join(" ")
            .toLowerCase()
            .includes(normalizedSearch);

        const matchesType =
          forumTypeFilter === "all" || post.forumType === forumTypeFilter;
        const matchesStatus =
          activeFilter === "all" ||
          (activeFilter === "active" ? post.isActive : !post.isActive);
        const matchesPostedBy =
          postedByFilter === "all" ||
          Number(post.postedBy) === Number(currentUserId);

        return matchesSearch && matchesType && matchesStatus && matchesPostedBy;
      })
      .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
  }, [
    activeFilter,
    currentUserId,
    forumState.posts,
    forumTypeFilter,
    postedByFilter,
    search,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPosts.length / FORUM_PAGE_SIZE),
  );

  const pagedPosts = filteredPosts.slice(
    (page - 1) * FORUM_PAGE_SIZE,
    page * FORUM_PAGE_SIZE,
  );

  const summary = useMemo(
    () => ({
      total: forumState.posts.length,
      referrals: forumState.posts.filter(
        (item) => item.forumType === "Job Referral",
      ).length,
      seeking: forumState.posts.filter(
        (item) => item.forumType === "Job Seeking",
      ).length,
      active: forumState.posts.filter((item) => item.isActive).length,
    }),
    [forumState.posts],
  );

  const resetDialog = () => {
    setDialogOpen(false);
    setEditingPostId(null);
    setFormData(emptyForm);
    setPostFormError("");
  };

  const handleOpenCreate = () => {
    setEditingPostId(null);
    setFormData(emptyForm);
    setPostFormError("");
    setDialogOpen(true);
  };

  const handleEditPost = (post) => {
    setEditingPostId(post.id);
    setPostFormError("");
    setFormData({
      forumType: post.forumType,
      jobTitle: post.jobTitle || "",
      jobDescription: post.jobDescription,
      experience: post.experience.toString(),
      companyName:
        post.forumType === "Job Seeking" ? "" : post.companyName,
      locationCity: post.locationCity,
      isActive: post.isActive,
      contactNumber: post.contactNumber,
    });
    setDialogOpen(true);
  };

  const handleSavePost = async () => {
    setPostFormError("");

    if (!currentUserToken) {
      setPostFormError("Logged-in session token is missing. Please sign in again.");
      return;
    }

    if (
      !formData.jobTitle.trim() ||
      !formData.jobDescription.trim() ||
      !formData.locationCity.trim()
    ) {
      setPostFormError(
        "Job title, job description, and location city are required.",
      );
      return;
    }

    if (formData.jobTitle.trim().length > 300) {
      setPostFormError("Job title cannot exceed 300 characters.");
      return;
    }

    if (
      formData.forumType === "Job Referral" &&
      !formData.companyName.trim()
    ) {
      setPostFormError("Company name is required.");
      return;
    }

    const requestBody = buildJobForumPostPayload(formData, currentUserId);
    const normalizedPost = {
      postedBy: currentUserId,
      posterName: currentUserName,
      passedOutYear: currentUserYear,
      forumType: requestBody.ForumType,
      jobTitle: requestBody.JobTitle,
      jobDescription: requestBody.JobDescription,
      experience: requestBody.Experience,
      companyName: requestBody.CompanyName,
      locationCity: requestBody.LocationCity,
      isActive: requestBody.IsActive,
      contactNumber: requestBody.ContactNumber,
    };

    try {
      setIsSubmittingPost(true);

      if (editingPostId) {
        const response = await api.updateJobForumPost(
          editingPostId,
          requestBody,
          currentUserToken,
        );
        const responseData = response?.data ?? {};

        setForumState((prev) => ({
          ...prev,
          posts: prev.posts.map((post) =>
            post.id === editingPostId
              ? {
                  ...post,
                  ...normalizedPost,
                  id: responseData.id ?? responseData.postId ?? post.id,
                  createdDate:
                    responseData.createdDate ??
                    responseData.CreatedDate ??
                    post.createdDate,
                }
              : post,
          ),
        }));
      } else {
        const response = await api.createJobForumPost(
          requestBody,
          currentUserToken,
        );
        const responseData = response?.data ?? {};

        setForumState((prev) => ({
          ...prev,
          posts: [
            {
              id: responseData.id ?? responseData.postId ?? Date.now(),
              createdDate:
                responseData.createdDate ??
                responseData.CreatedDate ??
                new Date().toISOString(),
              likedBy: [],
              ...normalizedPost,
            },
            ...prev.posts,
          ],
        }));
      }

      resetDialog();
    } catch (error) {
      console.error("Failed to save forum post", error);
      setPostFormError(
        error?.response?.data?.message ||
          "Unable to publish the post right now. Please try again.",
      );
    } finally {
      setIsSubmittingPost(false);
    }
  };

  const handleDeletePost = (postId) => {
    setForumState((prev) => ({
      ...prev,
      posts: prev.posts.filter((post) => post.id !== postId),
      comments: prev.comments.filter((comment) => comment.postId !== postId),
      likes: prev.likes.filter((like) => like.postId !== postId),
    }));
  };

  const handleToggleLike = async (postId) => {
    const existingLike = forumState.likes.find(
      (like) =>
        Number(like.postId) === Number(postId) &&
        Number(like.likedBy) === Number(currentUserId),
    );

    if (existingLike) {
      return;
    }

    try {
      setIsSubmittingLikeId(postId);
      const requestBody = {
        JobForumPostId: Number(postId),
        LikedBy: currentUserId,
      };
      const response = await api.createJobForumLike(requestBody, currentUserToken);
      const responseData = response?.data ?? {};

      setForumState((prev) => ({
        ...prev,
        likes: [
          ...prev.likes,
          {
            id: responseData.id ?? responseData.likeId ?? Date.now(),
            postId,
            likedBy: currentUserId,
          },
        ],
        posts: prev.posts.map((post) => {
          if (post.id !== postId) {
            return post;
          }

          const likedBy = Array.isArray(post.likedBy) ? post.likedBy : [];
          return likedBy.includes(currentUserId)
            ? post
            : { ...post, likedBy: [...likedBy, currentUserId] };
        }),
      }));
    } catch (error) {
      console.error("Failed to save forum like", error);
    } finally {
      setIsSubmittingLikeId(null);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [search, forumTypeFilter, activeFilter, postedByFilter]);

  return (
    <Box sx={{ bgcolor: "#f5f8ff", py: { xs: 4, md: 7 } }}>
      <Container maxWidth="xl">
        <Stack spacing={4}>
          <Card
            sx={{
              borderRadius: 5,
              overflow: "hidden",
              boxShadow: "0 18px 45px rgba(15, 23, 42, 0.1)",
            }}
          >
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                background:
                  "linear-gradient(135deg, #0f172a 0%, #1d4ed8 55%, #2563eb 100%)",
                color: "white",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={3}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Box>
                  <Chip
                    icon={<WorkIcon sx={{ color: "inherit !important" }} />}
                    label="Career Forum"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.14)",
                      color: "white",
                      mb: 2,
                    }}
                  />
                  <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
                    Job Referral and Job Seeking Forum
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "rgba(255,255,255,0.86)", maxWidth: 760 }}
                  >
                    Alumni can publish referrals or job-seeking posts, track
                    engagement through likes and comment counts, and continue
                    the full discussion on dedicated post pages.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreate}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1.2,
                    bgcolor: "white",
                    color: "#0f172a",
                    textTransform: "none",
                    "&:hover": { bgcolor: "#dbeafe" },
                  }}
                >
                  Create Forum Post
                </Button>
              </Stack>
            </Box>
          </Card>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Total Posts"
                value={summary.total}
                color="#1d4ed8"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Job Referrals"
                value={summary.referrals}
                color="#059669"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Job Seeking"
                value={summary.seeking}
                color="#7c3aed"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Active Posts"
                value={summary.active}
                color="#dc2626"
              />
            </Grid>
          </Grid>

          <Card
            sx={{
              borderRadius: 4,
              boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
            }}
          >
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search by company, city, poster, or description"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Forum Type</InputLabel>
                    <Select
                      label="Forum Type"
                      value={forumTypeFilter}
                      onChange={(event) =>
                        setForumTypeFilter(event.target.value)
                      }
                    >
                      <MenuItem value="all">All</MenuItem>
                      {FORUM_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={activeFilter}
                      onChange={(event) => setActiveFilter(event.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Posted By</InputLabel>
                    <Select
                      label="Posted By"
                      value={postedByFilter}
                      onChange={(event) => setPostedByFilter(event.target.value)}
                    >
                      <MenuItem value="all">Everyone</MenuItem>
                      <MenuItem value="me">Posted by me</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  size={{ xs: 12, md: 2 }}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setSearch("");
                      setForumTypeFilter("all");
                      setActiveFilter("active");
                      setPostedByFilter("all");
                    }}
                    sx={{ height: 56, textTransform: "none" }}
                  >
                    Reset Filters
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Stack spacing={3}>
            {isLoadingForum ? (
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
                }}
              >
                <CardContent>
                  <Typography color="text.secondary">
                    Loading forum posts...
                  </Typography>
                </CardContent>
              </Card>
            ) : null}
            {pagedPosts.map((post) => {
              const isOwner = Number(post.postedBy) === currentUserId;
              const likeCount = Array.isArray(post.likedBy)
                ? post.likedBy.length
                : 0;
              const isLiked =
                Array.isArray(post.likedBy) &&
                post.likedBy.includes(currentUserId);
              const commentCount = commentsCountMap[post.id] || 0;

              return (
                <Card
                  key={post.id}
                  sx={{
                    borderRadius: 4,
                    boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                    <Stack spacing={2.5}>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        justifyContent="space-between"
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ bgcolor: "#dbeafe", color: "#1d4ed8" }}>
                            {post.posterName?.charAt(0) || "A"}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h6"
                              fontWeight={800}
                              color="#0f172a"
                            >
                              {getForumTitle(post)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {post.forumType} • Posted by {post.posterName} •{" "}
                              {post.passedOutYear} • {formatForumDate(post.createdDate)}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <Chip
                            label={post.forumType}
                            color={
                              post.forumType === "Job Referral"
                                ? "success"
                                : "secondary"
                            }
                            variant="outlined"
                          />
                          <Chip
                            label={post.isActive ? "Active" : "Closed"}
                            color={post.isActive ? "primary" : "default"}
                          />
                          {isOwner ? (
                            <>
                              <Tooltip title="Edit post">
                                <IconButton
                                  onClick={() => handleEditPost(post)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete post">
                                <IconButton
                                  onClick={() => handleDeletePost(post.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : null}
                        </Stack>
                      </Stack>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 8 }}>
                          <Typography
                            variant="body1"
                            sx={{ color: "#334155", lineHeight: 1.75 }}
                          >
                            {post.jobDescription}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Card
                            variant="outlined"
                            sx={{
                              borderRadius: 3,
                              bgcolor: "#f8fafc",
                              borderColor: "rgba(148, 163, 184, 0.3)",
                            }}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Stack spacing={1.2}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Job Title
                                </Typography>
                                <Typography fontWeight={700}>
                                  {post.jobTitle || "Not provided"}
                                </Typography>
                                <Divider />
                                {post.forumType === "Job Referral" ? (
                                  <>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Company
                                    </Typography>
                                    <Typography fontWeight={700}>
                                      {post.companyName}
                                    </Typography>
                                    <Divider />
                                  </>
                                ) : null}
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Experience
                                </Typography>
                                <Typography fontWeight={700}>
                                  {post.experience} years
                                </Typography>
                                <Divider />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Location
                                </Typography>
                                <Typography fontWeight={700}>
                                  {post.locationCity}
                                </Typography>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      <Divider />

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <Stack direction="row" spacing={1}>
                          <Button
                            startIcon={isLiked ? <LikedIcon /> : <LikeIcon />}
                            onClick={() => handleToggleLike(post.id)}
                            disabled={
                              Boolean(isLiked) || isSubmittingLikeId === post.id
                            }
                            sx={{ textTransform: "none" }}
                          >
                            {likeCount} Likes
                          </Button>
                          <Button
                            component={RouterLink}
                            to={`/job-referral/${post.id}`}
                            startIcon={<CommentIcon />}
                            sx={{ textTransform: "none" }}
                          >
                            {commentCount} Comments
                          </Button>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Contact: {post.contactNumber || "Not shared"}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              page={page}
              count={totalPages}
              color="primary"
              onChange={(_, value) => setPage(value)}
            />
          </Box>
        </Stack>
      </Container>

      <Dialog open={dialogOpen} onClose={resetDialog} fullWidth maxWidth="md">
        <DialogTitle>
          {editingPostId ? "Edit Forum Post" : "Create Forum Post"}
        </DialogTitle>
        <DialogContent dividers>
          {postFormError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {postFormError}
            </Alert>
          ) : null}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Forum Type</InputLabel>
                <Select
                  label="Forum Type"
                  value={formData.forumType}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      forumType: event.target.value,
                    }))
                  }
                >
                  {FORUM_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Job Title"
                value={formData.jobTitle}
                inputProps={{ maxLength: 300 }}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    jobTitle: event.target.value.slice(0, 300),
                  }))
                }
                helperText={`${formData.jobTitle.length}/300 characters`}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.companyName}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    companyName: event.target.value,
                  }))
                }
                disabled={formData.forumType === "Job Seeking"}
                helperText={
                  formData.forumType === "Job Seeking"
                    ? "Not needed for job seeking posts."
                    : "Required for job referral posts."
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Experience"
                type="number"
                inputProps={{ min: 0, max: 99, step: 0.5 }}
                value={formData.experience}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    experience: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Location City"
                value={formData.locationCity}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    locationCity: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Contact Number"
                value={formData.contactNumber}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    contactNumber: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Active Post
                </Typography>
                <Switch
                  checked={formData.isActive}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      isActive: event.target.checked,
                    }))
                  }
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Job Description"
                value={formData.jobDescription}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    jobDescription: event.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialog} disabled={isSubmittingPost}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePost}
            disabled={isSubmittingPost}
            startIcon={
              isSubmittingPost ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {isSubmittingPost
              ? editingPostId
                ? "Updating..."
                : "Publishing..."
              : editingPostId
                ? "Update Post"
                : "Publish Post"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default JobReferal;
